import { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { operationsDialogOpenState, operationsDialogViewState } from '@chainlit/react-client';
import OperationsList from './OperationsList';
import OperationDetailView from './OperationDetailView';

/**
 * Modal dialog for displaying operations list and detail views.
 * Supports smooth transitions between list and detail views with fade animations.
 */
export default function OperationsDialog() {
  const [operationsDialogOpen, setOperationsDialogOpen] = useRecoilState(operationsDialogOpenState);
  const [dialogView, setDialogView] = useRecoilState(operationsDialogViewState);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayView, setDisplayView] = useState(dialogView);

  /**
   * Handles dialog close and resets view to list.
   */
  const handleClose = (open: boolean) => {
    if (!open) {
      setDialogView({ view: 'list', operationId: null });
    }
    setOperationsDialogOpen(open);
  };

  useEffect(() => {
    if (dialogView.view !== displayView.view || dialogView.operationId !== displayView.operationId) {
      setIsTransitioning(true);
      const fadeOutTimer = setTimeout(() => {
        setDisplayView(dialogView);
        const fadeInTimer = setTimeout(() => {
          setIsTransitioning(false);
        }, 50);
        return () => clearTimeout(fadeInTimer);
      }, 200);
      return () => clearTimeout(fadeOutTimer);
    }
  }, [dialogView, displayView]);

  const isDetailView = displayView.view === 'detail' && displayView.operationId;

  return (
    <Dialog open={operationsDialogOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[92vw] sm:w-[85vw] max-w-[1400px] h-[85vh] min-h-0 sm:min-h-[600px] flex flex-col gap-4 sm:gap-6 p-4 sm:p-8 box-border">
        <div 
          className="flex-1 min-h-0 overflow-y-auto transition-opacity duration-200"
          style={{ opacity: isTransitioning ? 0 : 1 }}
        >
          {isDetailView ? (
            <OperationDetailView 
              operationId={displayView.operationId!}
              skipInitialLoad={dialogView.view === 'detail' && dialogView.operationId === displayView.operationId}
            />
          ) : (
            <OperationsList />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

